import React, { useId, useMemo } from 'react';
import { Bar, Doughnut, Line, Scatter } from 'react-chartjs-2';
import type {
  ChartData,
  ChartOptions,
  ChartEvent,
  ActiveElement,
  Chart,
  LegendItem,
} from 'chart.js';
import type { ChartArtifactCallout, ChartArtifactPayload } from '@cotera/api';
import { cn } from '@cotera/client/v0/lib/utils';
import { toChartJsViewModel } from './chart-config';
import { ChartSparkPreview } from './chart-spark-preview';

type ChartViewProps = {
  payload: ChartArtifactPayload;
  className?: string;
  height?: number;
  animate?: boolean;
  showTitle?: boolean;
  onCategoryClick?: (label: string) => void;
  onSeriesClick?: (seriesLabel: string) => void;
};

function chartInstanceKey(payload: ChartArtifactPayload): string {
  return [
    payload.chart_type,
    payload.title,
    payload.data.labels.join('\0'),
    payload.data.datasets
      .map((ds) => `${ds.label ?? ''}:${ds.values.join(',')}`)
      .join('\0'),
    (payload.breakpoints ?? [])
      .map(
        (breakpoint) =>
          `${breakpoint.label}:${breakpoint.description ?? ''}:${
            breakpoint.x_label ?? ''
          }:${breakpoint.label_index ?? ''}:${breakpoint.color ?? ''}`
      )
      .join('\0'),
  ].join('|');
}

export const ChartView: React.FC<ChartViewProps> = ({
  payload,
  className,
  height = 280,
  animate = true,
  showTitle = true,
  onCategoryClick,
  onSeriesClick,
}) => {
  const reactId = useId();
  const chartDomId = `coco-chart-${reactId.replace(/:/g, '')}`;

  const viewModel = useMemo(() => toChartJsViewModel(payload), [payload]);
  const remountKey = useMemo(() => chartInstanceKey(payload), [payload]);
  const options = useMemo((): ChartOptions => {
    const base: ChartOptions = {
      ...viewModel.options,
      ...(animate === false ? { animation: false as const } : {}),
    };

    if (showTitle === false) {
      base.plugins = {
        ...base.plugins,
        title: { display: false },
      };
    }

    if (onSeriesClick !== undefined) {
      base.plugins = {
        ...base.plugins,
        legend: {
          ...(base.plugins?.legend ?? {}),
          onClick: (_event: ChartEvent, legendItem: LegendItem) => {
            const text = legendItem.text;
            if (text !== undefined && text !== '') {
              onSeriesClick(text);
            }
          },
        },
      };
    }

    if (onCategoryClick !== undefined) {
      base.onClick = (
        _event: ChartEvent,
        elements: ActiveElement[],
        chart: Chart
      ) => {
        const element = elements[0];
        if (element === undefined) {
          return;
        }
        const labels = chart.data.labels;
        if (labels === undefined) {
          return;
        }
        const raw = labels[element.index];
        const label =
          typeof raw === 'string'
            ? raw
            : raw !== null && raw !== undefined
            ? String(raw)
            : '';
        if (label !== '') {
          onCategoryClick(label);
        }
      };
    }

    return base;
  }, [viewModel.options, animate, showTitle, onCategoryClick, onSeriesClick]);

  const chart = (() => {
    switch (viewModel.chartKind) {
      case 'bar':
        return (
          <Bar
            key={remountKey}
            id={chartDomId}
            data={viewModel.data as ChartData<'bar'>}
            options={options as ChartOptions<'bar'>}
          />
        );
      case 'line':
        return (
          <Line
            key={remountKey}
            id={chartDomId}
            data={viewModel.data as ChartData<'line'>}
            options={options as ChartOptions<'line'>}
          />
        );
      case 'doughnut':
        return (
          <Doughnut
            key={remountKey}
            id={chartDomId}
            data={viewModel.data as ChartData<'doughnut'>}
            options={options as ChartOptions<'doughnut'>}
          />
        );
      case 'scatter':
        return (
          <Scatter
            key={remountKey}
            id={chartDomId}
            data={viewModel.data as ChartData<'scatter'>}
            options={options as ChartOptions<'scatter'>}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <div className={cn('w-full min-w-0', className)}>
      {payload.subtitle !== null && payload.subtitle !== '' && (
        <p className="mb-2 text-sm text-muted-foreground">{payload.subtitle}</p>
      )}
      {payload.callouts !== null && payload.callouts.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {payload.callouts.map((callout: ChartArtifactCallout) => (
            <div
              key={callout.label}
              className="rounded-md border border-border bg-muted/30 px-3 py-2"
            >
              <div
                className="text-sm font-semibold"
                style={
                  callout.color !== null && callout.color !== ''
                    ? { color: callout.color }
                    : undefined
                }
              >
                {callout.label}
              </div>
              {callout.description !== null && callout.description !== '' && (
                <div className="text-xs text-muted-foreground">
                  {callout.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ height }} className="w-full min-w-0">
        {chart}
      </div>
      {payload.options?.footnote !== null &&
        payload.options?.footnote !== undefined &&
        payload.options.footnote !== '' && (
          <p className="mt-2 text-xs text-muted-foreground">
            {payload.options.footnote}
          </p>
        )}
    </div>
  );
};

export const ChartInlinePreview: React.FC<{
  payload: ChartArtifactPayload;
  className?: string;
  onOpen?: () => void;
}> = ({ payload, className, onOpen }) => {
  const title = payload.title.trim();
  const firstCallout = payload.callouts?.[0];

  const content = (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      {title !== '' && (
        <p className="truncate text-sm font-medium text-foreground">{title}</p>
      )}
      {firstCallout !== undefined && (
        <p className="truncate text-xs text-muted-foreground">
          {firstCallout.label}
          {firstCallout.description !== null && firstCallout.description !== ''
            ? ` · ${firstCallout.description}`
            : ''}
        </p>
      )}
      <ChartSparkPreview payload={payload} />
    </div>
  );

  if (onOpen === undefined) {
    return content;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'w-full rounded-lg border border-input bg-background p-3 text-left shadow-xs transition-colors',
        'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className
      )}
    >
      {content}
      <span className="mt-2 block text-xs text-muted-foreground">
        Click to open full chart
      </span>
    </button>
  );
};

export const ChartArtifactView: React.FC<{
  payload: ChartArtifactPayload;
}> = ({ payload }) => {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto p-4">
      <ChartView payload={payload} height={420} />
    </div>
  );
};
